import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { CompactModuleFilter } from "../../../utils/RelatedEntries/CompactModuleFilter";
import { useRelatedCollection } from "../../../utils/RelatedEntries/useRelatedCollection";
import { RelatedEntries } from "../../../utils/RelatedEntries/RelatedEntries";
import { PMPagination } from "../../../utils/PMPagination";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../utils/PMTableView/RightDrawer";
import { mergeScopedFilters, useScopeFiltersSync } from "../../../utils/PMTableView/embedded-table-utils";
import { getServiceAgreementsAsync } from "../../../../slices/service/agreements";
import {
  NextJobCell,
} from "./table";
import SimplePrompt from "../../../utils/simplePrompt";
import {
  orderPath,
  reportPath,
} from "../../../Utils";
import {
  validateServiceAgreementCreateOrder,
  validateServiceAgreementCreateReport,
} from "./serviceAgreementNextJob";
import { runCreateNextJobOperation } from "./runCreateNextJobOperation";
import {
  ServiceAgreementNextServiceStatus,
  ServiceAgreementStatus,
} from "./utils";

export const ServiceAgreementsStackList = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <ServiceAgreementsStackListContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const ServiceAgreementsStackListContent = (props) => {
  const { filters: scopeFilters = [] } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [itemState, loadItems] = useRelatedCollection(getServiceAgreementsAsync, JSON.stringify(scopeFilters));
  const { isLoading, totalCount, currentCount } = itemState;

  const [drawerAgreement, setDrawerAgreement] = useState(null);
  const [pending, setPending] = useState(null);

  const [controller, setController] = useState(() => ({
    filters: mergeScopedFilters(scopeFilters, []),
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
    fields: "id,displayTitle,agreement_number,type,customer,project,delivery_address,next_service_date,interval,status,days_until_next_service,active_report,is_framework,shipping_address_valid,created_at",
  }));

  useScopeFiltersSync(scopeFilters, setController);

  const requestReport = (item) => {
    if (!validateServiceAgreementCreateReport(item)) return;
    setPending({ action: "report", item });
  };

  const requestOrder = (item) => {
    if (!validateServiceAgreementCreateOrder(item)) return;
    setPending({ action: "order", item });
  };

  const handleFiltersChange = (filters) => {
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: mergeScopedFilters(scopeFilters, filters.views?.length ? [{ name: "view", value: JSON.stringify(filters.views) }] : []),
    }));
  };

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => loadItems(ctrl), 250),
    [loadItems]
  );

  useEffect(() => {
    debouncedFetch(controller);
    return () => debouncedFetch.cancel();
  }, [controller, debouncedFetch]);

  const currentCompany = useSelector((state) => state.session.currentCompany);

  const runCreateReport = async () => {
    if (!pending?.item?.id) return;
    const agreement = pending.item;
    setPending(null);
    await runCreateNextJobOperation({
      jobKind: "report",
      agreement,
      companyId: currentCompany?.id,
      t,
      onCreated: (res) => navigate(reportPath({ report: res })),
      onRefresh: async () => { if (!await loadItems(controller)) throw new Error("Agreement list refresh failed"); },
    });
  };

  const runCreateOrder = async () => {
    if (!pending?.item?.id) return;
    const agreement = pending.item;
    setPending(null);
    await runCreateNextJobOperation({
      jobKind: "order",
      agreement,
      companyId: currentCompany?.id,
      t,
      onCreated: (res) => navigate(orderPath({ order: res })),
      onRefresh: async () => { if (!await loadItems(controller)) throw new Error("Agreement list refresh failed"); },
    });
  };

  return (
    <div className="min-w-0 space-y-4">
      <CompactModuleFilter
        disabled={isLoading}
        onFiltersChange={handleFiltersChange}
        initialFilters={{ query: controller.query || "", views: JSON.parse(controller.filters.find((filter) => filter.name === "view")?.value || "[]") }}
        searchPlaceholder={t("service.agreements.list.search_placeholder")}
        filterTitle={t("module_overview.filter_agreements")}
        hideStatusFilter
        customDropdownFilters={[{ key: "views", label: "relatedEntries.filters", options: [{ value: "ACTIVE", label: t("service.agreements.filters.active") }, { value: "CANCELLED", label: t("service.agreements.actions.cancelled") }, { value: "SERVICE_SOON", label: t("service.agreements.filters.service_soon") }] }]}
      />

      <div className="min-w-0">
        <RelatedEntries kind="agreements" context={props.module} data={itemState.data} isLoading={isLoading} error={itemState.error} totalCount={totalCount}
          onRowClick={setDrawerAgreement} onRetry={() => loadItems(controller)}
          renderStatus={(item) => <ServiceAgreementStatus serviceAgreement={item} />}
          renderActions={(item) => <NextJobCell item={item} onRequestReport={requestReport} onRequestOrder={requestOrder} />}
        />
        <div className="border-t border-slate-100 px-3 py-2">
          <PMPagination
            disabled={isLoading}
            onPageChange={(page) => setController((c) => ({ ...c, page }))}
            page={controller.page}
            perPage={controller.per_page}
            totalCount={totalCount}
            currentCount={currentCount}
            showCount
          />
        </div>
      </div>

      <RightDrawer
        open={!!drawerAgreement}
        onClose={() => setDrawerAgreement(null)}
        eyebrow={t("service.agreements.list.table_title")}
        title={
          drawerAgreement?.displayTitle ||
          drawerAgreement?.type?.displayTitle ||
          t("service.agreements.list.table_title")
        }
        actions={[
          {
            label: t("common.open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(`/dashboard/service/agreements/${drawerAgreement.id}`),
          },
        ]}
      >
        {drawerAgreement && (
          <>
            <DrawerSection title={t("common.details")}>
              <DrawerProps
                items={[
                  {
                    label: t("service.agreements.table.col_status"),
                    value: <ServiceAgreementStatus serviceAgreement={drawerAgreement} />,
                  },
                  {
                    label: t("service.agreements.filter.kind"),
                    value: drawerAgreement.type?.displayTitle || drawerAgreement.type?.title || "—",
                  },
                  {
                    label: t("service.agreements.table.col_next"),
                    value: drawerAgreement.next_service_date || "—",
                  },
                  {
                    label: t("service.agreements.table.col_days"),
                    value: (
                      <ServiceAgreementNextServiceStatus
                        serviceAgreement={drawerAgreement}
                      />
                    ),
                  },
                ]}
              />
            </DrawerSection>
            <DrawerSection title={t("service.agreements.table.col_next_job")}>
              <NextJobCell
                item={drawerAgreement}
                onRequestReport={requestReport}
                onRequestOrder={requestOrder}
              />
            </DrawerSection>
          </>
        )}
      </RightDrawer>

      <SimplePrompt
        open={pending?.action === "report"}
        onClose={() => setPending(null)}
        onAccept={runCreateReport}
        title={t("service.agreements.actions.prompt_report_title")}
        description={t("service.agreements.actions.prompt_report_body")}
      />
      <SimplePrompt
        open={pending?.action === "order"}
        onClose={() => setPending(null)}
        onAccept={runCreateOrder}
        title={t("service.agreements.actions.prompt_order_title")}
        description={t("service.agreements.actions.prompt_order_body")}
      />
    </div>
  );
};

ServiceAgreementsStackList.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "created_at",
};
